import App from 'app/app.js';
import googlepayTemplate from './google-pay.html';
import braintree from 'braintree-web';
import './google-pay.scss';

class GooglePayButtonController {
  isInitialized = false;
  isSupported = window.paymentDisplayGooglePayFlag === 'Y';
  GOOGLE_PAY_API_VERSION = 2;
  GOOGLE_PAY_MINOR_VERSION = 0;
  GOOGLE_PAY_MERCHANT_ID = 'BCR2DN7TZCS3RGIE';

  constructor(
    $rootScope,
    $scope,
    ShoppingCartStore,
    RouterService,
    PaymentService,
    JobStore,
    ErrorLogService,
    StatsdService,
    ContentStore,
    AnalyticsService,
    loggerService,
    StudentDetailService
  ) {
    this.$rootScope = $rootScope;
    this.$scope = $scope;
    this.ShoppingCartStore = ShoppingCartStore;
    this.RouterService = RouterService;
    this.PaymentService = PaymentService;
    this.JobStore = JobStore;
    this.ErrorLogService = ErrorLogService;
    this.loggerService = loggerService;
    this.StatsdService = StatsdService;
    this.ContentStore = ContentStore;
    this.AnalyticsService = AnalyticsService;
    this.StudentDetailService = StudentDetailService;
  }

  // eslint-disable-next-line max-lines-per-function
  $onInit() {
    if (!this.isSupported) {
      console.debug("Browser doesn't support apple pay, skipping");
      return;
    }
    if (!window.google) {
      this.ErrorLogService('Failed to load google pay script');
      this.loggerService.error('Failed to load google pay script');
      this.isSupported = false;
      this.initialized();
      return;
    }
    const googleClient = new window.google.payments.api.PaymentsClient({
      environment: window.paymentGooglePayEnvironment
    });
    const paymentInstancePromise = this.PaymentService.getPaymentInstancePromise();
    const shippingAddressRequired = this.ShoppingCartStore.hasShippingAddress();
    // eslint-disable-next-line promise/catch-or-return,max-lines-per-function
    paymentInstancePromise.then((clientInstance, err) => {
      if (err || window.braintreeAllPaymentsFailFlag === true) {
        const paymentError = err || 'All Payments loading failed';
        this.ErrorLogService(paymentError, 'Braintree client initialization error');
        this.loggerService.error(paymentError, 'Braintree client initialization error');
        this.StatsdService.incrementCounter('BRAINTREE_CLIENT_INITIALIZATION_FAILURE');
        this.initialized();
        return;
      }
      braintree.googlePayment.create(
        {
          client: clientInstance,
          googlePayVersion: this.GOOGLE_PAY_API_VERSION,
          googleMerchantId: this.GOOGLE_PAY_MERCHANT_ID
        },
        // eslint-disable-next-line max-lines-per-function
        (googlePaymentErr, googlePaymentInstance) => {
          googleClient
            .isReadyToPay({
              apiVersion: this.GOOGLE_PAY_API_VERSION,
              apiVersionMinor: this.GOOGLE_PAY_MINOR_VERSION,
              allowedPaymentMethods: googlePaymentInstance.createPaymentDataRequest({}).allowedPaymentMethods,
              existingPaymentMethodRequired: true
            })
            // eslint-disable-next-line max-lines-per-function
            .then((response) => {
              if (!response.result) {
                this.ErrorLogService('Failed to get results!');
                this.loggerService.error('Failed to get results!');
                this.initialized();
                return;
              }
              const googlePayButton = googleClient.createButton({
                buttonType: 'plain',
                buttonSizeMode: 'fill',
                onClick: () => {
                  this._handleClick(() =>
                    this.startPayment(googlePaymentInstance, googleClient, shippingAddressRequired)
                  );
                }
              });
              document.getElementById('google-pay-container').appendChild(googlePayButton);
              this.initialized();
            })
            .catch((err) => {
              this.ErrorLogService(err, 'Failed to init google');
              this.loggerService.error(err, 'Failed to init google');
              this.ShoppingCartStore.resetGooglePay();
              this.initialized();
            });
        }
      );
    });
  }

  _handleClick = (startPayment) => {
    const continuePayment = () => {
      this.preCheckout && this.preCheckout();
      if (this.preCheckoutValidation && !this.preCheckoutValidation()) {
        return;
      }

      startPayment();
    };

    if (this.express && this.StudentDetailService.shouldOpenStudentDetailModal()) {
      this.StudentDetailService.openStudentDetailModalWithCallback(continuePayment);
      return;
    }

    continuePayment();
  };

  startPayment = (googlePaymentInstance, googleClient, shippingAddressRequired) => {
    const total = this.ShoppingCartStore.getTotal().toFixed(2);
    const overrides = {
      transactionInfo: {
        currencyCode: this.JobStore.getCurrency(),
        totalPriceStatus: 'FINAL',
        totalPrice: `${total}`
      }
    };
    const paymentDataRequest = googlePaymentInstance.createPaymentDataRequest(overrides);
    if (this.requestAddress) {
      paymentDataRequest.emailRequired = true;
      paymentDataRequest.shippingAddressRequired = true;
      paymentDataRequest.shippingAddressParameters = {
        phoneNumberRequired: true
      };
      paymentDataRequest.allowedPaymentMethods.forEach((paymentMethod) => {
        paymentMethod.parameters.billingAddressRequired = true;
        paymentMethod.parameters.billingAddressParameters = {
          format: 'FULL',
          phoneNumberRequired: true
        };
      });
    }
    googleClient
      .loadPaymentData(paymentDataRequest)
      .then((paymentData) => {
        this.parseResponse(googlePaymentInstance, paymentData, shippingAddressRequired);
      })
      .catch((err) => {
        this.ErrorLogService(err, 'Failed to init google');
        this.loggerService.error(err, 'Failed to init google');
        this.ShoppingCartStore.resetGooglePay();
        this.initialized();
      });
  };

  parseResponse(googlePaymentInstance, paymentData, shippingAddressRequired) {
    googlePaymentInstance.parseResponse(paymentData, (err, result) => {
      if (err) {
        this.ErrorLogService(err, 'received error google pay result');
        this.loggerService.error(err, 'received error google pay result');
        this.initialized();
        return;
      }
      const paymentAddress = shippingAddressRequired
        ? paymentData.shippingAddress
        : paymentData.paymentMethodData.info.billingAddress;
      this.ShoppingCartStore.setGooglePay(
        result,
        this.requestAddress ? { ...paymentAddress, email: paymentData.email } : null
      );
      this.successPayment();
    });
  }

  successPayment = () => {
    if (this.express) {
      this.RouterService.goToBillingInfo();
    } else {
      this.RouterService.goToReviewOrder();
    }
    this.$rootScope.$digest();
  };

  initialized = () => {
    this.isInitialized = true;
    this.$scope.$applyAsync();
  };
}

GooglePayButtonController.$inject = [
  '$rootScope',
  '$scope',
  'ShoppingCartStore',
  'RouterService',
  'PaymentService',
  'JobStore',
  'ErrorLogService',
  'StatsdService',
  'ContentStore',
  'AnalyticsService',
  'loggerService',
  'StudentDetailService'
];

App.component('ltGooglePayButton', {
  template: googlepayTemplate,
  bindings: {
    selected: '<',
    click: '&',
    express: '<',
    preCheckout: '&?',
    preCheckoutValidation: '&?',
    requestAddress: '<'
  },
  transclude: true,
  controller: GooglePayButtonController,
  controllerAs: 'googlepay'
});
